import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { PolicyType, PolicyWithFilter, CDNRequestWeight } from './types';
import { TableProps, Typography, message } from 'antd';
import moment from 'moment';

const { baseUrl } = window.conviva;

export const formatDateTime = (isoString: string) => {
  if (!isoString) return '';
  return moment.utc(isoString).format('HH:mm:ss MMM D YYYY');
};

export const formatNum = (val: number) => {
  const num = +val;
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
};

export const columns: TableProps<PolicyType>['columns'] = [
  {
    title: 'ID',
    dataIndex: 'original_id',
    key: 'original_id',
    width: '10%',
    sorter: (a, b) => a.original_id - b.original_id,
  },
  {
    title: 'Rank',
    dataIndex: 'index',
    key: 'rankC',
    width: '10%',
    sorter: (a, b) => a.index - b.index,
  },
  {
    title: 'Policy Name',
    dataIndex: 'name',
    key: 'policyName',
    width: '30%',
    render: (text, record) => (
      <a
        href={`${baseUrl}/dashboard/policies-tracking/detail?policy_id=${record.policy_id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {text}
      </a>
    ),
  },
  {
    title: 'Requests per policy',
    dataIndex: 'requests_per_policy',
    key: 'requests',
    width: '25%',
    sorter: (a, b) => a.requests_per_policy - b.requests_per_policy,
  },
  {
    title: 'Last Updated',
    dataIndex: 'updated_at',
    key: 'updated_at',
    render: (text: string) => {
      return formatDateTime(text);
    },
    sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
  },
];

export const summaryColumns: ColumnsType<PolicyWithFilter> = [
  { title: 'ID', dataIndex: 'policy_id', key: 'policy_id', width: '10%' },
  { title: 'Rank', dataIndex: 'index', key: 'index', width: '10%' },
  {
    title: 'Last Updated',
    dataIndex: 'updated_at',
    key: 'updated_at',
    width: '20%',
    render: (text: string) => {
      return formatDateTime(text);
    },
  },
  { title: 'Filter', dataIndex: 'filter', key: 'filter', width: '20%' },
  {
    title: 'Filter Rule',
    dataIndex: 'filter_rule',
    key: 'filter_rule',
    ellipsis: true,
    render: (text) => <Typography.Text ellipsis={{ tooltip: text }}>{text}</Typography.Text>,
  },
];

export const timeSeriesConfig = (timeSeriesData, timeZone: number) => {
  const startDate = new Date(timeSeriesData[0][0]);
  const endDate = new Date(timeSeriesData[timeSeriesData.length - 1][0]);
  const diffInMs = endDate.getTime() - startDate.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);

  let dateFormat: Intl.DateTimeFormatOptions;

  if (diffInHours <= 24) {
    dateFormat = { hour: '2-digit', minute: '2-digit' };
  } else if (diffInHours < 24 * 7) {
    dateFormat = { weekday: 'short', hour: '2-digit', minute: '2-digit' };
  } else if (diffInHours <= 24 * 30) {
    dateFormat = { month: 'short', day: '2-digit' };
  } else {
    dateFormat = { month: 'short', day: '2-digit' };
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    ...dateFormat,
    timeZone: 'UTC',
  });

  const xAxisLabels = timeSeriesData.map(([timestamp]) => formatter.format(new Date(timestamp)));

  const seriesData = timeSeriesData.map(([timestamp, value]) => [
    formatter.format(new Date(timestamp)),
    value,
  ]);

  return {
    xAxis: {
      type: 'category',
      boundaryGap: false,
      name: `Time (UTC ${timeZone > 0 ? '+' : ''}${timeZone})`,
      nameLocation: 'middle',
      nameGap: 25,
      data: xAxisLabels,
    },
    yAxis: {
      type: 'value',
      name: 'Requests',
      nameLocation: 'middle',
      nameGap: 60,
      splitNumber: 4,
      axisLabel: {
        formatter: (value) => {
          if (value >= 1e6) return value / 1e6 + 'M';
          if (value >= 1e3) return value / 1e3 + 'K';
          return value;
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          width: 1,
          color: '#453d3d',
        },
      },
    },
    series: [
      {
        type: 'line',
        data: seriesData,
        showSymbol: false,
        smooth: false,
        lineStyle: {
          color: '#5dade2',
          width: 1,
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const point = params[0];
        const formatted = point.data[0];
        const value = point.data[1];
        return `${formatted}<br/>Requests: ${value}`;
      },
    },
    grid: {
      left: 80,
      right: 30,
      top: 10,
      bottom: 40,
    },
  };
};

export const getPieChartOption = (resources) => ({
  // tooltip: {
  //   trigger: 'item',
  //   formatter: ({ name, value, percent }) =>
  //     `${name}: ${formatNum(value)} (${percent.toFixed(1)}%)`,
  // },
  color: colors,
  series: [
    {
      type: 'pie',
      radius: ['20%', '80%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      label: { show: false },
      labelLine: { show: false },
      data: resources.map((item) => ({
        name: item.cdn_name,
        value: item.cdn_requests,
      })),
      emphasis: {
        scale: true,
        scaleSize: 8,
        label: {
          show: false,
        },
      },
    },
  ],
});

export const assignmentColumns: ColumnsType<CDNRequestWeight> = [
  { title: 'CDN', dataIndex: 'cdn', key: 'cdn' },
  { title: '% Configured', dataIndex: 'weight', key: 'weight', render: (text) => `${text / 100}` },
  {
    title: '% Actual',
    dataIndex: 'actual_weight',
    key: 'actual_weight',
    render: (value: number) => value.toFixed(2),
  },
];

export const colors = ['#1890ff', '#faad14', '#a0522d', '#52c41a', '#eb2f96', '#13c2c2', '#722ed1'];

export const checkDate = (start_day, end_day) => {
  if (!start_day && !end_day) {
    return true;
  }

  if (!start_day || !end_day) {
    message.error('Start date or end date is missing');
    return false;
  }

  if (moment(start_day).isAfter(moment(end_day))) {
    message.error('Start date must be earlier than or equal to end date');
    return false;
  }

  return true;
};

export const isPolicyMode = () => {
  return window.location.pathname.includes('/policies-tracking/');
};
